<template>
  <div ref="backgroundGallery" class="background-gallery">
    <div class="background-gallery__chip-tag-container background-gallery__main-chips">
      <file-selector :accept="backgroundAccept" @file-selected="uploadMedia">
        <chip-tag text="Upload">
          <plus-icon/>
        </chip-tag>
      </file-selector>
      <chip-tag
        text="Gallery"
        :is-active="backgroundSource === 'Gallery'"
        @click="setGalleryState('Gallery')"
      >
        <photo-icon />
      </chip-tag>
    </div>
    <div class="background-gallery__search">
      <search-bar
        :placeholder="placeholderText"
        :active="searchState !== 'inactive'"
        :show-clear-button="searchState !== 'inactive'"
        @focus="onSearchFocus"
        @search="onSearch"
        @empty-clear="setGalleryState(null)"
        v-model.trim="phrase"
      ></search-bar>
    </div>
    <div class="background-gallery__shadow"/> 
    <disclaimer class="background-gallery__collection-outage"  v-show="showContentOutageMsg" :message="'Some content is currently unavailable'" />
    <div class="background-gallery__search-chips" v-show="showChips('Search')">
      <div class="background-gallery__chip-tag-container">
        <chip-tag
        v-for="format in Object.values(SearchType)"
        :text="format"
        :key="`searchType-${format}`"
        :is-active="searchType === format"
        @click="onToggleSearchType(format)"
        />
      </div>
    </div>
    <div class="background-gallery__chip-tag-container background-gallery__collection-chips" v-show="showChips('Gallery')">
      <chip-tag
        v-for="collection in backgroundGalleryStore.collections"
        :text="collection.title"
        :key="collection.id"
        :is-active="selectedCollection?.title === collection.title && searchState === 'inactive'"
        @click="onSelectCollection(collection.title)"
      />
    </div>
    <disclaimer class="background-gallery__disclaimer" message="This content is royalty-free"/>
    <spinner
      class="background-gallery__spinner"
      v-show="isSearching"
    />
    <attribution-lozenge
      class="background-gallery__attribution"
      :attribution="attribution"
    />
    <mosaic-grid
      class="background-gallery__results"
      v-show="showResults"
      :items="backgroundGalleryStore.items"
      :num-cols="2"
      :gap="4"
    >
      <template #default="{ item, index }">
        <div
          :class="['background-gallery__results-item', {'background-gallery__results-item--selected': isSelectedBackground(item)}]"
          @click="selectBackground(item)"
        >
          <lazy-image
            class="background-gallery__results-item-contents"
            v-if="!item.preview.videoSource"
            :src="item.preview.imageSource.src"
            :parent-container="backgroundGallery"
            @viewport-intersect="onIntersect(index)"
            @dragstart="onDragBackground($event, item)"
          />
          <lazy-video
            v-else
            class="background-gallery__results-item-contents"
            draggable="true"
            :src="item.preview.videoSource.src"
            :thumbnail-src="item.preview.imageSource.src"
            :parent-container="backgroundGallery"
            @viewport-intersect="onIntersect(index)"
            @dragstart="onDragBackground($event, item)"
          />
        </div>
      </template>
    </mosaic-grid>
    <div
      class="background-gallery__no-results-text"
      v-show="hasNoResults"
    >No search results</div>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, computed } from 'vue'
import PlusIcon from '@/assets/PlusIcon.svg'
import PhotoIcon from '@/assets/PhotoIcon.svg'
import AttributionLozenge from '@/components/gallery/AttributionLozenge.vue'
import SearchBar from '@/components/gallery/SearchBar.vue'
import ChipTag from '@/components/gallery/ChipTag.vue'
import FileSelector from '@/components/common/FileSelector.vue'
import Spinner from '@/components/common/Spinner.vue'
import LazyImage from '@/components/gallery/LazyImage.vue'
import LazyVideo from '@/components/gallery/LazyVideo.vue'
import MosaicGrid from '@/components/gallery/MosaicGrid.vue'
import Disclaimer from '@/components/gallery/Disclaimer.vue'
import { storeToRefs } from 'pinia'
import { SearchType, useBackgroundGalleryStore } from '@/store/gallery/background-store'
import { useProjectStore } from '@/store/project-store'
import { useApplicationStore } from '@/store/application-store'
import { backgroundAccept } from '@/static/file-types'
import { GallerySource } from '@/store/gallery/types'
import { SearchState } from '@/types'
import GtmHelper from '@/analytics/gtm-helper'
import { useFileValidator, ValidationError } from '@/composables/UseFileValidator'
import useBackgroundGalleryItem from '@/composables/UseBackgroundGalleryItem'

type GalleryState = 'Gallery' | 'Search'

const gtmHelper = GtmHelper.getInstance()
const applicationStore = useApplicationStore()

const backgroundGalleryStore = useBackgroundGalleryStore()
const { searchType, selectedCollection, isCollectionsAvailable } = storeToRefs(backgroundGalleryStore)

const projectStore = useProjectStore()

const backgroundGallery = ref<HTMLElement | null>(null)
const backgroundSource = ref<GalleryState | null>(null)

const showChips = (state: GalleryState) => backgroundSource.value === state
const showContentOutageMsg = computed(() => !isCollectionsAvailable.value && (backgroundSource.value === 'Search' || backgroundSource.value === 'Gallery'))
const isSearching = computed(() => searchState.value === 'searching' || (backgroundGalleryStore.items.length === 0 && searchState.value === 'inactive'))
const showResults = computed(() => backgroundGalleryStore.items.length > 0)
const hasNoResults = computed(() => backgroundGalleryStore.items.length === 0 && searchState.value === 'searched')
const defaultSearchValue = 'music studio'
const placeholderText = computed(() => {
  let text = 'Search millions of videos and photos'
  if (searchState.value !== 'inactive') {
    switch (searchType.value) {
    case SearchType.VIDEO: {
      text = 'Search millions of videos'
      break
    }
    case SearchType.PHOTO: {
      text = 'Search millions of photos'
      break
    }
    }
  }
  return text
})

const attribution = computed(() => {
  let source: GallerySource = 'serato'
  if (searchState.value !== 'inactive') {
    source = 'pexels'
  } else if (selectedCollection.value) {
    source = selectedCollection.value.source
  }
  return source
})

const { validateBackgroundFile } = useFileValidator()
const { selectBackground, onDragBackground, isSelectedBackground } = useBackgroundGalleryItem()

onBeforeMount(async () => {
  // Fetch Pexel collections metadata and the default media
  await backgroundGalleryStore.fetchCollections()
  await retrieveActiveCollection('Vibes')

  // Preload gallery thumbnails
  if (!applicationStore.isActionMenuLoaded) {
    await Promise.allSettled(backgroundGalleryStore.items.map(item => {
      return fetch(item.preview.imageSource.src)
    }))
    applicationStore.isActionMenuLoaded = true
  }
})

const setGalleryState = (state: GalleryState | null) => {

  if (state !== 'Search') {
    searchState.value = 'inactive'
    phrase.value = ''
  }
  retrieveActiveCollection(backgroundSource.value !== null && selectedCollection.value ? selectedCollection.value.title : 'Vibes')
}

const uploadMedia = async (file: File) => {
  gtmHelper.triggerBackgroundUploadGtmEvent(file.size)
  try {
    await validateBackgroundFile(file)
    projectStore.setBackground(URL.createObjectURL(file), 'fill', file.name)
  } catch (e) {
    if (e instanceof ValidationError) {
      applicationStore.pushSnackbarMessage({
        header: e.title,
        text: e.message,
        state: 'error'
      })
    }
  }
}

const onSelectCollection = (title: string) => {
  if (title !== selectedCollection.value?.title) {
    retrieveActiveCollection(title)
  }
}

const retrieveActiveCollection = (title: string) => {
  const collection = backgroundGalleryStore.collections.find((collection) => collection.title === title)
  if (collection) {
    // Asynchronous call
    backgroundGalleryStore.fetchByCollection(collection)
  } else {
    backgroundGalleryStore.fetchByCollection(backgroundGalleryStore.collections[2]) //selecting trippy collection as the default one.
  }
}

const phrase = ref('')
const searchState = ref<SearchState>('inactive')

const onSearchFocus = () => {
  backgroundSource.value = 'Search'
  if (searchState.value === 'inactive') {
    retrieveDefaultResults()
  }
}

const onSearch = async () => {
  if (backgroundGalleryStore.searchPhrase !== phrase.value) {
    searchState.value = 'searching'
    await backgroundGalleryStore.fetchByKeyword(phrase.value || defaultSearchValue)
    searchState.value = 'searched'
    gtmHelper.triggerBackgroundSearchEvent(phrase.value)
  }
}

const onToggleSearchType = async (format: SearchType) => {
  searchType.value = format
  if (phrase.value) {
    searchState.value = 'searching'
    await backgroundGalleryStore.fetchByKeyword(phrase.value)
    searchState.value = 'searched'
  } else {
    // When no search term show default results
    retrieveDefaultResults()
  }
}

/**
 * Retrieves the default items to display in gallery on starting search
*/
const retrieveDefaultResults = async () => {
  searchState.value = 'searching'
  await backgroundGalleryStore.fetchByKeyword(defaultSearchValue)
  searchState.value = 'searched'
}

// Threshold for when to start fetching the next page, might change later
const threshold = 0.9

const onIntersect = (index: number) => {
  if (index === Math.floor((backgroundGalleryStore.items.length - 1) * threshold)) {
    backgroundGalleryStore.nextPage()
  }
}

const shadowTop = computed(() => ['Gallery', null].includes(backgroundSource.value) ? '64px' : '110px')
</script>

<style lang="scss">
.background-gallery {
  display: flex;
  flex-direction: column;
  padding: 0px 31px 32px 32px;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    display: none;
  }

  &__chip-tag-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__main-chips {
    margin-top: 32px;
    margin-bottom: 4px;
  }

  &__collection-chips {
    margin-top: 12px;
    margin-bottom: 12px;
  }

  &__search {
    // For the sticky searchbar
    position: sticky;
    top: 0;
    z-index: 3;
    background-color: $grey-85;
    padding: 12px 0px;
  }

  &__search-chips {
    padding-top: 12px;
    padding-bottom: 12px;
    position: sticky;
    top: 52px;
    z-index: 3;
    background-color: $grey-85;
  }

  &__shadow {
    // Sticky positioning, and show on scroll
    margin-top: -12px;
    position: sticky;
    top: v-bind(shadowTop);
    z-index: 2;
    flex-shrink: 0;

    height: 12px;
    width: 100%;
    background-image: linear-gradient(to bottom, rgba($black, 0.5), rgba($black, 0));
  }

  &__collection-outage {
    color: $grey-10;
    font-size: 14px;
    margin-top: 12px;
    line-height: 18px;
  }

  &__disclaimer {
    margin-top: 4px;
    line-height: 18px;
  }

  &__spinner,
  &__no-results-text {
    margin-top: 36px;
  }

  &__spinner {
    height: 34px;
    width: 34px;
    margin-left: auto;
    margin-right: auto;
  }

  &__attribution {
    position: absolute;
    bottom: 12px;
    right: 12px;
    z-index: 2;
  }

  &__no-results-text {
    color: $white;
    font-size: 14px;
    text-align: center;
  }

  &__results {
    margin-top: 8px;

    &-item, &-item-contents {
      height: 100%;
      width: 100%;
      border-radius: 4px;
    }

    &-item {
      position: relative;
      vertical-align: text-bottom;
      background-color: $grey-90;

      &::after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          border-radius: inherit;
          border: 1px solid transparent;
          z-index: 1;
          transition: border 150ms $animationCurve;
          pointer-events: none;
      }

      &:hover {
        &::after {
          border: 1px solid $white;
        }
      }

      //mousedown
      &:active::after {
        border: 3px solid $white;
      }

      &--selected, &--selected:hover {
        &::after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          border-radius: inherit;
          border: 2px solid $white;
          z-index: 1;
          pointer-events: none;
        }
      }
    }
  }
}
</style>

